import React, { useEffect, useState } from "react";
import { Modal, Select, SelectItem } from "carbon-components-react";

import {
  OwnedRealEstate,
  ReoOwnershipStatusDisplayValues,
  ReoOwnershipStatuses,
  SandboxInputsForREOStatuses,
  SandboxRequestREO,
} from "@eave/shared/types/api";

import { dollarsTo2Places } from "../helpers";

import { reoToDisplayAddressParts } from "./helpers";

const styles = require("./styles.module.scss");

const getOldAndNewStatuses = (
  oldReos: SandboxRequestREO[],
  newReos: SandboxRequestREO[],
): [string, string] => {
  for (let i = 0, l = oldReos.length; i < l; i++) {
    const oldReo = oldReos[i];
    const newReo = newReos.find(reo => reo.id === oldReo.id);

    if (oldReo.ownership_status !== newReo.ownership_status) {
      return [oldReo.ownership_status, newReo.ownership_status];
    }
  }
  return ["", ""];
};

interface InputsForReos {
  total_assets: number;
  monthly_liabilities: number;
  monthly_income: number;
}

interface FormValues extends InputsForReos {
  reos: SandboxRequestREO[];
}

interface Props {
  ownedRealEstate: OwnedRealEstate[]; // TODO: jjw: used for addresses; put in SandboxRequestREO?
  values: FormValues;
  disabled: boolean;
  getInputsForReos(_: SandboxRequestREO[]): Promise<SandboxInputsForREOStatuses>;
  onUpdate(_: FormValues): void;
}

const ReosInput = ({ ownedRealEstate, values, disabled, getInputsForReos, onUpdate }: Props) => {
  const { reos, total_assets, monthly_liabilities, monthly_income } = values;

  const [newValues, setNewValues] = useState<FormValues | null>(null);

  useEffect(() => {
    setNewValues(null);
  }, [reos, total_assets, monthly_liabilities, monthly_income]);

  if (!reos.length) {
    return (
      <div className={styles.container}>
        <span>No REOs on loan application</span>
      </div>
    );
  }

  const inputsHaveChanged = (newInputs: InputsForReos): boolean =>
    newInputs.total_assets !== total_assets ||
    newInputs.monthly_liabilities !== monthly_liabilities ||
    newInputs.monthly_income !== monthly_income;

  const onUpdateReosOnly = (newReos: SandboxRequestREO[]) =>
    onUpdate({
      ...values,
      reos: newReos,
    });

  const onChangeSelect = (reo: SandboxRequestREO, e) => {
    const { value } = e.target;

    if (value === reo.ownership_status || value === "") {
      return;
    }

    const newReos = reos.map(r => ({
      id: r.id,
      ownership_status: r.id === reo.id ? value : r.ownership_status,
    }));

    getInputsForReos(newReos).then(newInputs => {
      if (inputsHaveChanged(newInputs)) {
        setNewValues({
          reos: newReos,
          total_assets: newInputs.total_assets,
          monthly_liabilities: newInputs.monthly_liabilities,
          monthly_income: newInputs.monthly_income,
        });
      } else {
        onUpdateReosOnly(newReos);
      }
    });
  };

  const onCancelModal = () => setNewValues(null);

  const modalOpen = !!newValues;

  return (
    <div className={styles.container}>
      {modalOpen && (
        <Modal
          open
          modalHeading="Update inputs for changed REO ownership status?"
          primaryButtonText="Yes, update"
          secondaryButtons={[
            {
              buttonText: "Undo change",
              onClick: onCancelModal,
            },
            {
              buttonText: "Don't update",
              onClick: () => onUpdateReosOnly(newValues.reos),
            },
          ]}
          onRequestSubmit={() => onUpdate(newValues)}
          onRequestClose={onCancelModal}
        >
          <div className={styles.modal}>
            <div>
              REOs can contribute to assets, liabilities, and income depending on their ownership
              statuses.
            </div>
            <div>
              Based on the change you just made, TRUS would like to update the following inputs:
            </div>
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Old</th>
                  <th>New</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Ownership status</td>
                  {getOldAndNewStatuses(reos, newValues.reos).map(s => (
                    <td key={s}>{ReoOwnershipStatusDisplayValues[s]}</td>
                  ))}
                </tr>
                {newValues.total_assets !== total_assets && (
                  <tr>
                    <td>Total assets</td>
                    <td>{dollarsTo2Places(total_assets)}</td>
                    <td>{dollarsTo2Places(newValues.total_assets)}</td>
                  </tr>
                )}
                {newValues.monthly_liabilities !== monthly_liabilities && (
                  <tr>
                    <td>Monthly liabilities</td>
                    <td>{dollarsTo2Places(monthly_liabilities)}</td>
                    <td>{dollarsTo2Places(newValues.monthly_liabilities)}</td>
                  </tr>
                )}
                {newValues.monthly_income !== monthly_income && (
                  <tr>
                    <td>Monthly income</td>
                    <td>{dollarsTo2Places(monthly_income)}</td>
                    <td>{dollarsTo2Places(newValues.monthly_income)}</td>
                  </tr>
                )}
              </tbody>
            </table>
            <div>
              You can update these inputs, not update them, or undo your status change altogether.
            </div>
          </div>
        </Modal>
      )}
      {reos.map((reo: SandboxRequestREO) => (
        <div key={reo.id} className={styles.reo}>
          <ol>
            {reoToDisplayAddressParts(ownedRealEstate.find(r => r.id === reo.id)).map(part => (
              <li key={part}>{part}</li>
            ))}
          </ol>
          <div className={styles.type}>
            <Select
              id={`reo-${reo.id}`}
              name={`reo_${reo.id}`}
              value={reo.ownership_status}
              invalid={!reo.ownership_status}
              invalidText="must select ownership status"
              onChange={e => onChangeSelect(reo, e)}
              disabled={modalOpen || disabled}
              light
            >
              {/* cannot submit with unknown status, but here in case an reo loads with it */}
              {/* operator would need to fix status in other part of app before submitting form */}
              <SelectItem key="unknown" text="—" value="" />
              {Object.keys(ReoOwnershipStatuses).map(s => (
                <SelectItem key={s} text={ReoOwnershipStatusDisplayValues[s] || s} value={s} />
              ))}
            </Select>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ReosInput;
